import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import { useWidgetConfig } from '../../hooks/useWidgetConfig';
import './ProductCarousel.css';

/**
 * ProductCarousel - カルーセル型アフィリエイト商品ウィジェット
 * スワイプ対応の横スクロール商品表示
 *
 * 既存運用方式:
 * - もしも tracking URL (`https://af.moshimo.com/af/c/click?...`) を
 *   `product.affiliateLinks.moshimo.url` に保存し、img + button のカード形式で表示。
 * - ボタンクリックで window.open → もしも 側でクリックが追跡される。
 *
 * @param {string} location - 表示場所 ('form_page' | 'mypage' | 'recipe_page')
 * @param {string} category - カテゴリー ('醤油' | '味噌' | '塩' | '油' など)
 * @param {number} limit - 表示する商品数（デフォルト: 5）
 * @param {string} title - セクション見出し（省略時: '私たちが食卓で選んでいるもの'）
 */
const ProductCarousel = ({ location, category, limit = 5, title }) => {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const widgetConfig = useWidgetConfig();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragMoved, setDragMoved] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, category, limit]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      let response;

      // カテゴリー指定がある場合はカテゴリーAPIを使用
      if (category) {
        response = await api.get(`/api/affiliate/products/category/${category}`, {
          params: { limit }
        });
      } else if (location) {
        // 場所指定の場合は既存のlocation APIを使用
        response = await api.get(`/api/affiliate/products/location/${location}`, {
          params: { limit }
        });
      }

      if (response?.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('商品読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  // マウス/タッチドラッグ開始
  const handleDragStart = (e) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setDragMoved(false);
    const pageX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    setStartX(pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  // マウス/タッチドラッグ中
  const handleDragMove = (e) => {
    if (!isDragging || !carouselRef.current) return;
    const pageX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    const x = pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (Math.abs(walk) > 5) setDragMoved(true);
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  // マウス/タッチドラッグ終了
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // スクロール位置から現在の active index を算出（モバイルインジケータ用）
  const handleScroll = () => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const firstCard = container.querySelector('.product-carousel__card');
    if (!firstCard) return;
    const computedStyle = window.getComputedStyle(container);
    const gap = parseInt(computedStyle.gap, 10) || 16;
    const cardWidth = firstCard.offsetWidth + gap;
    const index = Math.round(container.scrollLeft / cardWidth);
    setActiveIndex(Math.max(0, Math.min(index, products.length - 1)));
  };

  // 矢印ナビ: 1カード分スクロール
  const scrollByCard = (direction) => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const firstCard = container.querySelector('.product-carousel__card');
    if (!firstCard) return;
    const computedStyle = window.getComputedStyle(container);
    const gap = parseInt(computedStyle.gap, 10) || 16;
    const cardWidth = firstCard.offsetWidth + gap;
    container.scrollBy({
      left: direction * cardWidth,
      behavior: 'smooth',
    });
  };

  // 商品クリック追跡
  const handleProductClick = async (productId, affiliateUrl, e) => {
    // ドラッグ中の誤クリックは無視
    if (dragMoved) {
      e?.preventDefault();
      return;
    }
    if (!affiliateUrl) {
      console.warn('ProductCarousel: affiliateUrl が未設定です', productId);
      return;
    }
    try {
      // クリック数を記録
      await api.post('/api/affiliate/click', {
        productId,
        source: category ? `category_${category}` : location,
        affiliateType: 'moshimo',
        clickedUrl: affiliateUrl
      });
    } catch (error) {
      console.error('クリック追跡エラー:', error);
      // エラーでも遷移は行う
    }
    window.open(affiliateUrl, '_blank', 'noopener,noreferrer');
  };

  // 表示対象プランの判定（管理画面 productWidget.showForPlans に従う）
  // config 読み込み前は無料プランのみ表示（保守的デフォルト）
  // productWidget.enabled が false の場合も非表示
  const productWidget = widgetConfig?.productWidget;
  if (productWidget) {
    if (productWidget.enabled === false) return null;
    const allowedPlans = (productWidget.showForPlans && productWidget.showForPlans.length > 0)
      ? productWidget.showForPlans
      : ['free'];
    if (!allowedPlans.includes(userPlan)) return null;
  } else {
    // config 未取得時のフォールバック
    if (userPlan !== 'free') return null;
  }

  if (loading || products.length === 0) {
    return null;
  }

  return (
    <section className="product-carousel">
      <h3 className="product-carousel__title">
        {title || '私たちが食卓で選んでいるもの'}
      </h3>

      {/* 矢印ナビ（PC のみ・カード数 2以上の時のみ表示） */}
      {products.length >= 2 && (
        <>
          <button
            type="button"
            className="product-carousel__nav-btn product-carousel__nav-btn--prev"
            onClick={() => scrollByCard(-1)}
            aria-label="前の商品"
          >
            ‹
          </button>
          <button
            type="button"
            className="product-carousel__nav-btn product-carousel__nav-btn--next"
            onClick={() => scrollByCard(1)}
            aria-label="次の商品"
          >
            ›
          </button>
        </>
      )}

      <div
        className="product-carousel__container"
        ref={carouselRef}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onScroll={handleScroll}
      >
        {products.map((product) => (
          <article key={product._id} className="product-carousel__card">
            <div className="product-carousel__image-wrapper">
              {product.images?.primary?.url ? (
                <img
                  src={product.images.primary.url}
                  alt={product.images?.primary?.altText || product.name}
                  className="product-carousel__image"
                  loading="lazy"
                />
              ) : (
                <div className="product-carousel__image-placeholder">画像なし</div>
              )}
            </div>
            <div className="product-carousel__content">
              <h4 className="product-carousel__product-name">{product.name}</h4>
              {product.description && (
                <p className="product-carousel__description">{product.description}</p>
              )}
              <button
                type="button"
                className="product-carousel__button"
                onClick={(e) => handleProductClick(
                  product._id,
                  product.affiliateLinks?.moshimo?.url,
                  e
                )}
              >
                詳細を見る →
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* モバイル用ナビ（カード数 2以上の時のみ表示）：矢印 + ドット */}
      {products.length >= 2 && (
        <div className="product-carousel__mobile-nav">
          <button
            type="button"
            className="product-carousel__mobile-arrow"
            onClick={() => scrollByCard(-1)}
            aria-label="前の商品"
          >
            ‹
          </button>
          {products.length >= 3 && (
            <div className="product-carousel__indicator" aria-hidden="true">
              {products.map((_, idx) => (
                <span
                  key={idx}
                  className={`product-carousel__indicator-dot ${
                    idx === activeIndex ? 'product-carousel__indicator-dot--active' : ''
                  }`}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            className="product-carousel__mobile-arrow"
            onClick={() => scrollByCard(1)}
            aria-label="次の商品"
          >
            ›
          </button>
        </div>
      )}

      <p className="product-carousel__disclaimer">
        PR・広告 ／ ※紹介する商品は橋本病の治療・改善を目的としたものではありません
      </p>
    </section>
  );
};

export default ProductCarousel;
